import React, { useEffect, useState } from 'react';
import { Button, Grid, InputAdornment, Typography } from '@material-ui/core';
import Edit from '@material-ui/icons/Edit';
import { Alert } from '@material-ui/lab';
import { IconButton, LoadingBounce, TextField } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetPrices } from 'hooks/useGetPrices';
import hash from 'object-hash';
import { Part, PartPrices, PqlPriceType } from 'types/UFOTypes';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { getTAXES, getTotalPartTAX } from 'utils/taxes';
import Taxes from './Taxes';

interface PriceProps {
  status: string;
  isService?: boolean;
  printMode?: boolean;
  setPqlByMspn?: (mspn: string, price: number) => void;
  setPqlById?: (id: number, price: number) => void;
  canEdit?: boolean;
  submitButton?: boolean;
  wizardColors?: boolean;
  qty: number;
  mspn: string;
  id?: number;
  prices?: Part;
  setIsLoading?: (l: boolean) => void;
  commonPricesParams: CommonPricesParamsType;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  is_pql: boolean;
  wizardMode?: boolean;
  pqlPrice?: number;
  manualPrice?: boolean;
  reqPqlMode?: boolean;
  isEditing?: boolean;
  setIsEditingPqlPrice?: (isEditing: boolean) => void;
}
export function Price(props: PriceProps) {
  const { t } = useTranslation();
  const isDealer = useCurrentLocationIsDealer();

  const {
    printMode = false,
    status,
    isService = false,
    setPqlByMspn,
    setPqlById,
    submitButton = !setPqlById && !setPqlByMspn,
    wizardColors,
    qty,
    mspn,
    id,
    setIsLoading,
    commonPricesParams,
    is_pql: isPql,
    wizardMode,
    manualPrice,
    reqPqlMode,
    isEditing,
    setIsEditingPqlPrice,
    pqlPrice: pqlPriceProp,
    prices: pricesProp,
    canEdit: canEditProp,
  } = props;

  const [pqlPrice, setPqlPrice] = useState<number | undefined>(pqlPriceProp);
  const [error, setError] = useState(false);
  const [editPql, setEditPql] = useState<boolean>();
  const [pqlProvided, setPqlProvided] = useState(false);
  const [pqlPrices, setPqlPrices] = useState<PqlPriceType>();
  const [prices, setPrices] = useState<PartPrices | undefined>(pricesProp);
  const canEdit = canEditProp && isPql;

  const pricesResponse = useGetPrices({
    ...commonPricesParams,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    parts: manualPrice ? [] : [{ mspn, qty, is_pql: isPql }],
  });

  useEffect(() => {
    if (setIsEditingPqlPrice && editPql !== undefined) setIsEditingPqlPrice(editPql);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPql]);

  useEffect(() => {
    if (setIsLoading) setIsLoading(pricesResponse.isLoading);
    if (pricesResponse?.data?.parts?.length && hash(pricesResponse?.data?.parts[0]) !== hash(prices || {}))
      setPrices(pricesResponse.data.parts[0]);
    if (pricesResponse?.data?.pqlParts?.length && hash(pricesResponse?.data?.pqlParts[0]) !== hash(pqlPrices || {})) {
      setPqlPrices(pricesResponse.data.pqlParts[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricesResponse]);

  useEffect(() => {
    if (setPqlById && id && pqlPrice && !submitButton) {
      setPqlById(id, pqlPrice);
    }
    if (setPqlByMspn && mspn && pqlPrice && !submitButton) {
      setPqlByMspn(mspn, pqlPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pqlPrice]);

  const partPrice = pqlProvided ? pqlPrice : pqlPrices?.unitAmout || Number.parseFloat(prices?.invoice_price || '0');

  useEffect(() => {
    const orderPrice = Number.parseFloat(pricesProp?.invoice_price || '0');
    if (pqlProvided && orderPrice !== pqlPrice) {
      setPqlProvided(false);
      setPqlPrice(orderPrice);
    }
    if (pricesProp) setPrices(pricesProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricesProp]);

  useEffect(() => {
    if (pqlPriceProp) {
      setPqlPrice(pqlPriceProp);
      setPqlProvided(true);
      setEditPql(false);
    }
  }, [pqlPriceProp]);

  if (pricesResponse.isLoading && (!isDealer || isService)) return <LoadingBounce />;

  if (!prices && !pqlPrices && !pqlPrice && !editPql) return null;

  const finalInvoicePrice = prices?.final_invoice_price ? Number(prices.final_invoice_price) : undefined;

  const fet = prices?.fet || 0;

  let partsPrice = '0';
  if (partPrice && !pqlPrice) partsPrice = (partPrice * qty).toFixed(2);
  if (pqlPrice) partsPrice = (pqlPrice * qty).toFixed(2);
  if (pqlPrices?.unitAmout && !pqlPrice) partsPrice = pqlPrices?.unitAmout.toFixed(2);

  const getTotal = () => {
    if (pqlPrice) return pqlPrice * qty;
    if (finalInvoicePrice) return finalInvoicePrice;
    if (partPrice) return partPrice * qty;
    return 0;
  };
  const total = getTotal();

  const taxes = prices ? getTAXES(prices, qty) : null;
  const totalTax = prices ? getTotalPartTAX(prices, qty) : null;

  if (error) {
    return (
      <Alert
        severity="error"
        onClose={() => {
          setError(false);
        }}
      >
        {t('Error at price edition')}
      </Alert>
    );
  }

  if (!partPrice && !isPql) return null;

  let alert: { message: string; severity: 'success' | 'warning' | 'info' | 'error' } = {
    message: '',
    severity: 'info',
  };
  if (!isDealer && isPql && !partPrice && !wizardMode) alert = { message: t('Local pricing requested'), severity: 'info' };
  if (isDealer && isPql && !partPrice && !reqPqlMode && !wizardMode && !isEditing)
    alert = { message: t('Local pricing not provided'), severity: 'info' };
  if (!isDealer && isPql && !partPrice && wizardMode) alert = { message: t('Request local price'), severity: 'info' };
  const renderSubmitButton = (
    <>
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={() => {
          if (setPqlById && id) {
            setPqlById(id, pqlPrice || 0);
            setPqlProvided(true);
            setEditPql(false);
          }
          if (setPqlByMspn && mspn) {
            setPqlByMspn(mspn, pqlPrice || 0);
            setPqlProvided(true);
            setEditPql(false);
          }
        }}
        disabled={!pqlPrice && !pqlPrices?.amount}
        style={{
          margin: 8,
          height: 20,
          width: wizardMode || reqPqlMode ? undefined : 'calc(100% - 90px)',
          minWidth: 45,
          fontSize: 10,
          whiteSpace: 'nowrap',
        }}
      >
        {t('Add price')}
      </Button>
      {editPql && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={() => {
            setPqlPrice(pqlPriceProp);
            setEditPql(false);
            setPqlProvided(true);
          }}
          style={{
            margin: 8,
            marginTop: 4,
            height: 20,
            width: wizardMode || reqPqlMode ? undefined : 'calc(100% - 90px)',
            minWidth: 45,
            fontSize: 10,
          }}
        >
          {t('Cancel')}
        </Button>
      )}
    </>
  );

  return (
    <>
      {alert.message && (
        <>
          <Alert
            severity={alert.severity}
            style={{
              margin: 0,
              padding: '0px 8px',
              textAlign: 'center',
              fontSize: 11,
            }}
          >
            {t(alert.message)}
          </Alert>
          <br />
        </>
      )}
      {isDealer &&
        isPql &&
        ((!partPrice && !pqlProvided) || editPql) &&
        canEdit &&
        (isEditing || reqPqlMode || wizardMode) && (
          <>
            <Grid container spacing={2} style={{ paddingTop: 0 }}>
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'end' }}>
                {!pqlPrice && (
                  <Alert
                    severity="warning"
                    style={{
                      margin: 0,
                      padding: '0px 8px',
                      textAlign: 'center',
                      fontSize: 14,
                      width: 220,
                    }}
                  >
                    {t('Please provide local price')}
                  </Alert>
                )}
              </Grid>
              <Grid container spacing={1} item xs={wizardMode ? 8 : 12}>
                <Grid item xs={6} container direction="row" alignItems="center" justify="flex-end">
                  <Typography style={{ fontSize: 11 }}>{t('Unit price: ')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    style={{ width: submitButton && !reqPqlMode ? 74 : undefined }}
                    type="number"
                    placeholder={partPrice?.toFixed(2)}
                    value={pqlPrice}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment style={{ transform: 'scale(0.8)' }} position="start">
                          $
                        </InputAdornment>
                      ),
                      style: { fontSize: 12 },
                    }}
                    onChange={(e) => {
                      const positiveValue = Number(e.target.value);
                      if (positiveValue > 0) setPqlPrice(positiveValue);
                      if (!positiveValue) setPqlPrice(undefined);
                    }}
                  />
                </Grid>
                <Grid item xs={6} container direction="row" alignItems="center" justify="flex-end">
                  <Typography style={{ fontSize: 11 }}>{t('Total: ')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    readOnly
                    style={{ width: submitButton && !reqPqlMode ? 74 : undefined }}
                    type="number"
                    placeholder={((pqlPrice || partPrice || 0) * qty).toFixed(2)}
                    value={total}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment style={{ transform: 'scale(0.8)' }} position="start">
                          $
                        </InputAdornment>
                      ),
                      style: { fontSize: 12 },
                    }}
                    onChange={(e) => {
                      const positiveValue = Number(e.target.value);
                      if (positiveValue > 0) setPqlPrice(positiveValue);
                      if (!positiveValue) setPqlPrice(undefined);
                    }}
                  />
                </Grid>
              </Grid>
              {wizardMode && (
                <Grid item xs={4}>
                  {renderSubmitButton}
                </Grid>
              )}
            </Grid>
            {submitButton && !wizardMode && renderSubmitButton}
          </>
        )}
      {(partPrice || pqlProvided) && isService && !editPql && (
        <>
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
              }}
            >
              <div>
                <Typography noWrap style={{ fontSize: 12, fontWeight: 'bold' }}>
                  {t('Unit price: ${{partPrice}}', { partPrice: partPrice?.toFixed(2) })}
                </Typography>
                {!!prices && Number.parseFloat(prices.fet || '0') > 0 && (
                  <Typography noWrap style={{ fontSize: 12 }}>
                    {t('FET: ${{fet}}', { fet: Number.parseFloat(prices.fet || '0').toFixed(2) })}
                  </Typography>
                )}
                {!!taxes && !!totalTax && taxes.length > 0 && (
                  <Taxes taxes={taxes} printMode={printMode} totalTax={totalTax} status={status} />
                )}
                <Typography
                  noWrap
                  color={!wizardColors || printMode ? undefined : 'error'}
                  style={{ fontSize: wizardColors ? 14 : 12, fontWeight: wizardColors ? undefined : 'bold' }}
                >
                  {t('Total: ${{partsPrice}}', { partsPrice: isService ? total.toFixed(2) : partsPrice })}
                </Typography>
              </div>
              {isDealer && canEdit && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditPql(true);
                    setPqlProvided(false);
                  }}
                  color="primary"
                  style={{
                    transform: 'scale(0.8)',
                    marginLeft: 8,
                  }}
                >
                  <Edit />
                </IconButton>
              )}
            </div>
            {isPql && !reqPqlMode && (
              <div
                style={{
                  position: 'relative',
                  height: 35,
                  marginBottom: 8,
                  width: '100%',
                }}
              >
                <Typography
                  style={{
                    fontSize: 10,
                    maxWidth: 210,
                    position: 'absolute',
                    right: 0,
                    marginTop: 8,
                    textAlign: 'left',
                  }}
                >
                  {t('Local taxes not included. They will be calculated and displayed on your final invoice')}
                </Typography>
              </div>
            )}
          </div>
        </>
      )}
      {!!partPrice && !isService && (
        <>
          <Typography noWrap style={{ fontSize: 12, fontWeight: 'bold' }}>
            {t('Invoice price: ${{partPrice}}', { partPrice: partPrice.toFixed(2) })}
          </Typography>
          {Number.parseFloat(fet || '0') > 0 && (
            <>
              {t('FET: ${{fet}}', { fet: Number.parseFloat(fet || '0').toFixed(2) })}
              <br />
            </>
          )}
          {!!taxes && !!totalTax && taxes.length > 0 && (
            <Taxes taxes={taxes} printMode={printMode} totalTax={totalTax} status={status} />
          )}
          {!!finalInvoicePrice && (
            <Typography
              noWrap
              color={!wizardColors || printMode ? undefined : 'error'}
              style={{ fontSize: wizardColors ? 14 : 12, fontWeight: wizardColors ? undefined : 'bold' }}
            >
              {t('Total: ${{totalPrice}}', {
                totalPrice: Number(prices?.final_invoice_price).toFixed(2),
              })}
            </Typography>
          )}
        </>
      )}
    </>
  );
}
